import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Building2, Save, UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { FormActions, FormField, FormPageHeader, FormSection } from '@/components/common/FormSection'
import { AdditionalInfoSection, ContactsSection, PrimaryEmailSection, WhatsAppSection } from '@/components/clients/ClientFormSections'
import { clientService } from '../services/client-service'
import { Client, Contact, UpdateClientDto, PersonalData, CompanyData } from '../types/client'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'

import { getErrorMessage } from '../utils/error.utils'
export function EditClientPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { prepareDateForInput, prepareDateForSubmit } = useDateTimeFormat()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [primaryEmail, setPrimaryEmail] = useState('')

  const [formData, setFormData] = useState({
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE' | 'BLOCKED' | 'PROSPECT',
    whatsappPhone: '',
    tags: [] as string[],
    notes: '',
    personalData: {
      fullName: '',
      preferredName: '',
      cpf: '',
      rg: '',
      birthDate: '',
      nationality: '',
      profession: ''
    },
    companyData: {
      companyName: '',
      tradeName: '',
      cnpj: '',
      stateRegistration: '',
      municipalRegistration: '',
      foundedDate: '',
      activitySector: '',
      legalRepresentative: ''
    },
    contacts: [] as Contact[]
  })

  const loadClient = useCallback(async (clientId: string) => {
    try {
      setLoading(true)
      setError(null)
      const clientData = await clientService.getClientById(clientId)
      setClient(clientData)

      // Extract primary email and filter it from contacts
      const emailContact = clientData.contacts?.find(c => c.contactType === 'EMAIL' && c.isPrimary)
      const otherContacts = clientData.contacts?.filter(c => !(c.contactType === 'EMAIL' && c.isPrimary)) || []

      setPrimaryEmail(emailContact?.value || '')

      // Populate form with client data
      setFormData({
        status: clientData.status,
        whatsappPhone: clientData.whatsappPhone || '',
        tags: clientData.tags || [],
        notes: clientData.notes || '',
        personalData: clientData.personalData ? {
          fullName: clientData.personalData.fullName || '',
          preferredName: clientData.personalData.preferredName || '',
          cpf: clientData.personalData.cpf || '',
          rg: clientData.personalData.rg || '',
          birthDate: prepareDateForInput(clientData.personalData.birthDate),
          nationality: clientData.personalData.nationality || '',
          profession: clientData.personalData.profession || ''
        } : {
          fullName: '', preferredName: '', cpf: '', rg: '', birthDate: '',
          nationality: '', profession: ''
        },
        companyData: clientData.companyData ? {
          companyName: clientData.companyData.companyName || '',
          tradeName: clientData.companyData.tradeName || '',
          cnpj: clientData.companyData.cnpj || '',
          stateRegistration: clientData.companyData.stateRegistration || '',
          municipalRegistration: clientData.companyData.municipalRegistration || '',
          foundedDate: prepareDateForInput(clientData.companyData.foundedDate),
          activitySector: clientData.companyData.activitySector || '',
          legalRepresentative: clientData.companyData.legalRepresentative || ''
        } : {
          companyName: '', tradeName: '', cnpj: '', stateRegistration: '',
          municipalRegistration: '', foundedDate: '', activitySector: '', legalRepresentative: ''
        },
        contacts: otherContacts
      })
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar dados do contato'))
    } finally {
      setLoading(false)
    }
  }, [prepareDateForInput])

  useEffect(() => {
    if (id) {
      loadClient(id)
    }
  }, [id, loadClient])

  const handleInputChange = (field: string, value: unknown, section?: 'personalData' | 'companyData') => {
    if (section) {
      setFormData({
        ...formData,
        [section]: {
          ...formData[section],
          [field]: value
        }
      })
    } else {
      setFormData({
        ...formData,
        [field]: value
      })
    }
  }

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [
        ...formData.contacts,
        {
          contactType: 'WHATSAPP',
          label: '',
          value: '',
          isPrimary: false,
          notes: ''
        } as Contact
      ]
    })
  }

  const updateContact = (index: number, field: string, value: unknown) => {
    const updatedContacts = [...formData.contacts]
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value
    }

    setFormData({
      ...formData,
      contacts: updatedContacts
    })
  }

  const removeContact = (index: number) => {
    const updatedContacts = formData.contacts.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      contacts: updatedContacts
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!client || !id) return

    setSaving(true)
    setError(null)

    try {
      const stripEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> | undefined => {
        const cleaned: Partial<T> = {}
        for (const key of Object.keys(obj) as (keyof T)[]) {
          const value = obj[key]
          if (value !== '' && value !== null && value !== undefined) {
            cleaned[key] = value
          }
        }
        return Object.keys(cleaned).length > 0 ? cleaned : undefined
      }

      const cleanedContacts: NonNullable<UpdateClientDto['contacts']> = formData.contacts
        .filter(contact => contact.value.trim())
        .map(contact => ({
          contactType: contact.contactType,
          value: contact.value,
          isPrimary: contact.isPrimary,
          isVerified: contact.isVerified,
          ...(contact.label?.trim() ? { label: contact.label } : {}),
          ...(contact.notes?.trim() ? { notes: contact.notes } : {})
        }))

      if (primaryEmail && primaryEmail.trim()) {
        cleanedContacts.unshift({
          contactType: 'EMAIL',
          value: primaryEmail.trim(),
          isPrimary: true,
          isVerified: false
        })
      }

      const updateData: UpdateClientDto = {
        id,
        status: formData.status,
        tags: formData.tags,
        contacts: cleanedContacts
      }

      if (formData.whatsappPhone && formData.whatsappPhone.trim()) {
        updateData.whatsappPhone = formData.whatsappPhone.trim()
      }

      if (formData.notes && formData.notes.trim()) {
        updateData.notes = formData.notes.trim()
      }

      if (client.clientType === 'PERSON') {
        const cleanedPersonalData = stripEmpty<Omit<PersonalData, 'clientId'>>({
          fullName: formData.personalData.fullName,
          firstName: formData.personalData.fullName.split(' ')[0] || '',
          lastName: formData.personalData.fullName.split(' ').slice(1).join(' '),
          preferredName: formData.personalData.preferredName,
          cpf: formData.personalData.cpf?.replace(/\D/g, ''),
          rg: formData.personalData.rg,
          birthDate: prepareDateForSubmit(formData.personalData.birthDate) ?? undefined,
          nationality: formData.personalData.nationality,
          profession: formData.personalData.profession
        })

        if (cleanedPersonalData) {
          updateData.personalData = cleanedPersonalData as Omit<PersonalData, 'clientId'>
        }
      } else {
        const cleanedCompanyData = stripEmpty<Omit<CompanyData, 'clientId'>>({
          companyName: formData.companyData.companyName,
          tradingName: formData.companyData.tradeName,
          tradeName: formData.companyData.tradeName,
          cnpj: formData.companyData.cnpj?.replace(/\D/g, ''),
          stateRegistration: formData.companyData.stateRegistration,
          municipalRegistration: formData.companyData.municipalRegistration,
          foundedDate: prepareDateForSubmit(formData.companyData.foundedDate) ?? undefined,
          activitySector: formData.companyData.activitySector,
          legalRepresentative: formData.companyData.legalRepresentative
        })

        if (cleanedCompanyData) {
          updateData.companyData = cleanedCompanyData as Omit<CompanyData, 'clientId'>
        }
      }

      await clientService.updateClient(id, updateData)
      navigate(`/clients/${id}`)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao atualizar contato'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-72 max-w-4xl" />
      </div>
    )
  }

  if (!client) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        {error && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
        )}
        <p className="py-12 text-center text-muted-foreground">Contato não encontrado</p>
      </div>
    )
  }

  const textField = (
    id: string,
    label: string,
    field: string,
    section: 'personalData' | 'companyData',
    options: { type?: string; required?: boolean; placeholder?: string; className?: string } = {}
  ) => {
    const data = formData[section] as Record<string, string>
    return (
      <FormField label={label} htmlFor={id} className={options.className}>
        <Input
          id={id}
          type={options.type ?? 'text'}
          required={options.required}
          placeholder={options.placeholder}
          value={data[field]}
          onChange={(e) => handleInputChange(field, e.target.value, section)}
        />
      </FormField>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <FormPageHeader
        title="Editar Contato"
        description={`#${client.clientNumber}`}
        icon={client.clientType === 'PERSON' ? UserCheck : Building2}
        backTo={`/clients/${id}`}
        backLabel="Voltar"
      />

      <form onSubmit={handleSubmit} className="flex max-w-4xl flex-col gap-4">
        {client.clientType === 'PERSON' ? (
          <FormSection title="Dados Pessoais" icon={UserCheck} className="md:grid-cols-2">
            {textField('client-full-name', 'Nome Completo *', 'fullName', 'personalData', { required: true, className: 'md:col-span-2' })}
            {textField('client-preferred-name', 'Nome Preferido', 'preferredName', 'personalData')}
            {textField('client-cpf', 'CPF', 'cpf', 'personalData', { placeholder: '000.000.000-00' })}
            {textField('client-rg', 'RG', 'rg', 'personalData')}
            {textField('client-birth-date', 'Data de Nascimento', 'birthDate', 'personalData', { type: 'date' })}
            {textField('client-nationality', 'Nacionalidade', 'nationality', 'personalData')}
            {textField('client-profession', 'Profissão', 'profession', 'personalData')}
          </FormSection>
        ) : (
          <FormSection title="Dados da Empresa" icon={Building2} className="md:grid-cols-2">
            {textField('company-name', 'Razão Social *', 'companyName', 'companyData', { required: true })}
            {textField('company-trade-name', 'Nome Fantasia', 'tradeName', 'companyData')}
            {textField('company-cnpj', 'CNPJ', 'cnpj', 'companyData', { placeholder: '00.000.000/0001-00' })}
            {textField('company-activity-sector', 'Setor de Atividade', 'activitySector', 'companyData')}
            {textField('company-founded-date', 'Data de Fundação', 'foundedDate', 'companyData', { type: 'date' })}
            {textField('company-legal-representative', 'Representante Legal', 'legalRepresentative', 'companyData')}
          </FormSection>
        )}

        <WhatsAppSection id="client-whatsapp" value={formData.whatsappPhone} onChange={(value) => handleInputChange('whatsappPhone', value)} />
        <PrimaryEmailSection id="client-primary-email" value={primaryEmail} onChange={setPrimaryEmail} />
        <ContactsSection contacts={formData.contacts} onAdd={addContact} onUpdate={updateContact} onRemove={removeContact} />
        <AdditionalInfoSection
          statusId="client-status"
          notesId="client-notes"
          status={formData.status}
          notes={formData.notes}
          onStatusChange={(status) => handleInputChange('status', status)}
          onNotesChange={(notes) => handleInputChange('notes', notes)}
        />

        {error && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
        )}

        <FormActions>
          <Button variant="outline" asChild><Link to={`/clients/${id}`}>Cancelar</Link></Button>
          <Button type="submit" disabled={saving} loading={saving}>
            {!saving && <Save className="size-4" />}
            Salvar Alterações
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
